import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'react-day-picker/dist/style.css';
// Shell eager (pequeno) — o resto é code-splitted.
import Login from './pages/Login';
import MainLayout from './components/layout/MainLayout';
import { Toaster } from './components/ui/toaster';
import { usePermissions } from "@/hooks/usePermissions";
import { refreshCurrentUser } from "@/services/api/auth";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import NavigationReport from "@/pages/NavigationReport";
import LeadLifetime from "@/pages/LeadLifetime";
import EmailV1 from "./pages/EmailV1/EmailV1.tsx";

// Todas as páginas são lazy — cada tela vira um chunk carregado sob demanda,
// pra que o load inicial não baixe o app inteiro (recharts, mapas, xyflow,
// lucide etc. ficam nos chunks das telas que os usam, não no bundle principal).
// `lazyNamed` cobre páginas com export nomeado (barris).
const lazyNamed = (loader: () => Promise<any>, name: string) =>
    lazy(() => loader().then((m) => ({ default: m[name] })));

const TvMode = lazy(() => import('@/pages/TvMode/TvMode'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard.tsx'));
const Dominios = lazy(() => import('./pages/Dominios'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const Projetos = lazy(() => import('./pages/Projetos'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const Profile = lazy(() => import('./pages/Profile'));
// Conexões Meta v2 (handoff_meta_v2, etapa 02). A tela antiga (pages/Conexoes) fica no repo, sem rota, até o fim do pacote.
const Conexoes = lazy(() => import('@/pages/Meta/Connections'));
const CustomTargeting = lazy(() => import('@/pages/Gam/CustomValues'));
const MetaCampaigns = lazy(() => import('@/pages/Meta/Campaigns/MetaCampaigns.tsx'));
const Chatpions = lazy(() => import('@/pages/Chatpion/Chatpions.tsx'));
const MetaDirect = lazy(() => import('@/pages/MetaDirect/MetaDirect.tsx'));
const MetaOverall = lazy(() => import('@/pages/MetaOverall/MetaOverall.tsx'));
const MetaFlow = lazy(() => import('@/pages/MetaFlow/MetaFlow.tsx'));
const MetaCampaignHistory = lazy(() => import('@/pages/MetaCampaignHistory/MetaCampaignHistory.tsx'));
const MetaOverallV2 = lazy(() => import('@/pages/MetaOverallV2/MetaOverallV2.tsx'));
const DashboardV2 = lazy(() => import('./pages/DashboardV2/DashboardV2'));
const TopUrl = lazy(() => import('@/pages/Analytics/TopUrls/TopUrls'));
const GamAdvertisers = lazy(() => import('@/pages/GamAdvertisers/GamAdvertisers.tsx'));
const AdvertiserAnalysis = lazy(() => import('@/pages/GamAdvertisers/AdvertiserAnalysis.tsx'));
const CreateRotativeLink = lazy(() => import('@/pages/Analytics/RotativeLinks/CreateManual'));
const RotativeLinks = lazy(() => import('@/pages/Analytics/RotativeLinks/List'));
const EditRotativeLink = lazy(() => import('@/pages/Analytics/RotativeLinks/Edit'));
const CreateAutoRotativeLink = lazy(() => import('@/pages/Analytics/RotativeLinks/CreateAuto'));
const Apps = lazy(() => import('@/pages/Apps/Apps.tsx'));
const Notifications = lazy(() => import('@/pages/Notifications/Notifications.tsx'));
const NotificationsGoals = lazy(() => import('@/pages/NotificationsGoals/NotificationsGoals.tsx'));
const NotificationsChannels = lazy(() => import('@/pages/NotificationsChannels/NotificationsChannels.tsx'));
const CampaignLinks = lazy(() => import('@/pages/Meta/CampaignLinks/CampaignLinks.tsx'));
const AccountReport = lazy(() => import('@/pages/Meta/Accounts/Accounts.tsx'));
const Setup = lazy(() => import('@/pages/PushNotification/Setup/Setup.tsx'));
const Campaigns = lazy(() => import('@/pages/PushNotification/Campaigns/Campaigns.tsx'));
const CampaignCreate = lazy(() => import('@/pages/PushNotification/CampaignCreate/CampaignCreate.tsx'));
const Leads = lazy(() => import('@/pages/PushNotification/Leads/Leads.tsx'));
const Dash = lazy(() => import('@/pages/PushNotification/Dashboard/Dashboard.tsx'));
const FlowList = lazyNamed(() => import('@/pages/PushNotification/Flow'), 'FlowList');
const FlowCreate = lazyNamed(() => import('@/pages/PushNotification/Flow'), 'FlowCreate');
const FlowBuilder = lazyNamed(() => import('@/pages/PushNotification/Flow'), 'FlowBuilder');
const FlowFunnel = lazyNamed(() => import('@/pages/PushNotification/Flow'), 'FlowFunnel');
const PushDocs = lazy(() => import('@/pages/PushNotification/Docs/PushDocs.tsx'));
const PushCaptureDomainsList = lazy(() => import('@/pages/PushCapture/v2/Domains/Domains'));
const PushCaptureDomainDetail = lazy(() => import('@/pages/PushCapture/v2/Domains/Detalhe'));
const PushCaptureGroupsList = lazy(() => import('@/pages/PushCapture/v2/Groups/Groups'));
const PushCaptureTemplatesList = lazy(() => import('@/pages/PushCapture/v2/Templates/Templates'));
const PushCaptureTemplateBuilder = lazy(() => import('@/pages/PushCapture/v2/Editor/TemplateBuilder'));
const SmsDashboard = lazyNamed(() => import('@/pages/SMS'), 'SmsDashboard');
const SmsCampaigns = lazyNamed(() => import('@/pages/SMS'), 'SmsCampaigns');
const SmsCampaignDetail = lazyNamed(() => import('@/pages/SMS'), 'SmsCampaignDetail');
const SmsCampaignCreate = lazyNamed(() => import('@/pages/SMS'), 'SmsCampaignCreate');
const SmsBroadcasts = lazyNamed(() => import('@/pages/SMS'), 'SmsBroadcasts');
const SmsBroadcastDetail = lazyNamed(() => import('@/pages/SMS'), 'SmsBroadcastDetail');
const SmsBroadcastCreate = lazyNamed(() => import('@/pages/SMS'), 'SmsBroadcastCreate');
const SmsFlows = lazyNamed(() => import('@/pages/SMS'), 'SmsFlows');
const SmsFlowEditor = lazyNamed(() => import('@/pages/SMS'), 'SmsFlowEditor');
const SmsLeads = lazyNamed(() => import('@/pages/SMS'), 'SmsLeads');
const SmsLeadDetail = lazyNamed(() => import('@/pages/SMS'), 'SmsLeadDetail');
const SmsProviders = lazyNamed(() => import('@/pages/SMS'), 'SmsProviders');
const SmsDomainKeys = lazyNamed(() => import('@/pages/SMS'), 'SmsDomainKeys');
const SmsCosts = lazyNamed(() => import('@/pages/SMS'), 'SmsCosts');
const SmsReports = lazyNamed(() => import('@/pages/SMS'), 'SmsReports');
const SmsRoi = lazyNamed(() => import('@/pages/SMS'), 'SmsRoi');
const SmsFlowDetail = lazyNamed(() => import('@/pages/SMS'), 'SmsFlowDetail');
const WppModule = lazyNamed(() => import('@/pages/WhatsApp'), 'WppModule');
const WppOverview = lazyNamed(() => import('@/pages/WhatsApp'), 'WppOverview');
const WppNumbers = lazyNamed(() => import('@/pages/WhatsApp'), 'WppNumbers');
const WppNumberDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppNumberDetail');
const WppPoolDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppPoolDetail');
const WppGroupSetDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppGroupSetDetail');
const WppGroups = lazyNamed(() => import('@/pages/WhatsApp'), 'WppGroups');
const WppGroupDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppGroupDetail');
const WppContacts = lazyNamed(() => import('@/pages/WhatsApp'), 'WppContacts');
const WppContactDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppContactDetail');
const WppListDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppListDetail');
const WppSegmentEditor = lazyNamed(() => import('@/pages/WhatsApp'), 'WppSegmentEditor');
const WppBroadcasts = lazyNamed(() => import('@/pages/WhatsApp'), 'WppBroadcasts');
const WppBroadcastEditor = lazyNamed(() => import('@/pages/WhatsApp'), 'WppBroadcastEditor');
const WppBroadcastDetail = lazyNamed(() => import('@/pages/WhatsApp'), 'WppBroadcastDetail');
const WppBroadcastReportRedirect = lazyNamed(() => import('@/pages/WhatsApp'), 'WppBroadcastReportRedirect');
const WppSettings = lazyNamed(() => import('@/pages/WhatsApp'), 'WppSettings');
const WppFlows = lazyNamed(() => import('@/pages/WhatsApp'), 'WppFlows');
const WppFlowEditor = lazyNamed(() => import('@/pages/WhatsApp'), 'WppFlowEditor');
const WppFlowExecutions = lazyNamed(() => import('@/pages/WhatsApp'), 'WppFlowExecutions');
const WppFlowEditRedirect = lazyNamed(() => import('@/pages/WhatsApp'), 'WppFlowEditRedirect');
const WppRedirect = lazy(() => import('@/pages/WhatsApp').then((m) => ({ default: m.WppRedirect })));
const WppInbox = lazyNamed(() => import('@/pages/WhatsApp'), 'WppInbox');
const WppAi = lazyNamed(() => import('@/pages/WhatsApp'), 'WppAi');
const EmployeePermissions = lazyNamed(() => import('@/pages/Employees'), 'EmployeePermissions');
const PermissionGroups = lazyNamed(() => import('@/pages/PermissionGroups'), 'PermissionGroups');
const MenuManagement = lazyNamed(() => import('@/pages/MenuManagement'), 'MenuManagement');
const CreateCampaignPage = lazy(() => import('@/pages/Meta/Create/NovaCampanha/NovaCampanha'));
const EsteiraWizardPage = lazy(() => import('@/pages/Meta/Create/NovaEsteira/NovaEsteira'));
const EsteiraDetailPage = lazy(() => import('@/pages/Meta/Create/DetalheEsteira/DetalheEsteira'));
// Gerenciador: segue a tela antiga por enquanto (a nova, no visual v2, está na branch design/meta-v2-gerenciador).
const GerenciadorPage = lazy(() => import('@/pages/MetaGerenciador/GerenciadorPage'));
// Google Ads v2 (handoffs/google-ads-v2)
const GoogleConexoes = lazy(() => import('@/pages/GoogleAdsV2/ConnectionsPage.tsx'));
const GoogleCampaigns = lazy(() => import('@/pages/GoogleAdsV2/CampaignsPage.tsx'));
const GoogleAdGroups = lazy(() => import('@/pages/GoogleAdsV2/AdGroupsPage.tsx'));
const GoogleAds = lazy(() => import('@/pages/GoogleAdsV2/AdsPage.tsx'));
const GooglePlacements = lazy(() => import('@/pages/GoogleAdsV2/PlacementsPage.tsx'));
const GamBlocks = lazy(() => import('@/pages/Gam/Blocks/Blocks'));
const RewardsDomains = lazy(() => import('@/pages/Rewards/v2/Domains/Domains'));
const RewardsList = lazy(() => import('@/pages/Rewards/v2/Rewards/Rewards'));
const RewardsCaptureKeys = lazy(() => import('@/pages/Rewards/v2/CaptureKeys/CaptureKeys'));
const RewardEdit = lazy(() => import('@/pages/Rewards/v2/Editor/RewardEdit'));
const RewardsDashboard = lazy(() => import('@/pages/Rewards/v2/Dashboard/Dashboard'));
const RewardsLeads = lazy(() => import('@/pages/Rewards/v2/Leads/Leads'));
const AnalyticsDashboard = lazy(() => import('@/pages/Analytics/Dashboard/Dashboard'));
const LeadJourney = lazy(() => import('@/pages/Analytics/LeadJourney/LeadJourney'));
const SetupConnecta = lazy(() => import('@/pages/Gam/Setup/Editor/SetupDominio'));
const ConnectaSetups = lazy(() => import('@/pages/Gam/Setup/List'));

// Configurar QueryClient com persistência no localStorage
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutos
            gcTime: 1000 * 60 * 60 * 24, // 24 horas
        },
    },
});

// Componente para proteger rotas que precisam de autenticação
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const token = localStorage.getItem('access_token');

    if (!token) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
}

// Componente para proteger rotas por permissão específica
// Busca permissões atualizadas do servidor para garantir que mudanças feitas pelo admin sejam refletidas
function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
    const { hasPermission, isEmployee } = usePermissions();
    const [checked, setChecked] = React.useState(false);
    const [allowed, setAllowed] = React.useState(true);

    React.useEffect(() => {
        if (!isEmployee) {
            setChecked(true);
            setAllowed(true);
            return;
        }
        let cancelled = false;
        refreshCurrentUser().then(() => {
            if (cancelled) return;
            // Re-ler permissões do localStorage atualizado
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                const perms = new Set<string>(user?.permissions || []);
                setAllowed(perms.has('*') || perms.has(permission));
            } else {
                setAllowed(false);
            }
            setChecked(true);
        });
        return () => { cancelled = true; };
    }, [permission, isEmployee]);

    if (!checked) return null;
    if (!allowed && !hasPermission(permission)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
}

// Bloqueia colaboradores (employees) — espelha o require_owner_or_partner do backend.
// Usado em páginas administrativas (Apps, Gerenciar Menus, detalhe de projeto com membros).
function OwnerOrPartnerRoute({ children }: { children: React.ReactNode }) {
    const { isEmployee } = usePermissions();

    if (isEmployee) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
}

// As telas Tráfego Direto e Esteiras agora vivem sob o Gerenciador (toggles). As rotas antigas
// redirecionam pra lá pré-selecionando a aba — assim os botões "Voltar" existentes (que apontam
// pra /meta/optimizer e /meta/esteiras) caem no Gerenciador, não numa tela solta antiga.
function RedirectToGerenciadorTab({ tab }: { tab: 'trafego' | 'direto' | 'automatico' }) {
    localStorage.setItem('gerenciador_tab', tab);
    return <Navigate to="/meta/gerenciador" replace />;
}

// Fallback enquanto o chunk da tela carrega (leve, sem libs pesadas).
function PageLoading() {
    return (
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="w-6 h-6 border-2 border-muted border-t-emerald-500 rounded-full animate-spin" />
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Suspense fallback={<PageLoading />}>
                <Routes>
                    <Route path="/login" element={<Login />} />

                    {/* Modo TV — tela cheia, sem sidebar/header */}
                    <Route
                        path="/tv"
                        element={
                            <ProtectedRoute>
                                <TvMode />
                            </ProtectedRoute>
                        }
                    />

                    {/* WhatsApp: editor de fluxo em tela cheia (sem sidebar/header; plano-front §2.4). WppModule próprio
                        fornece projeto + tempo real. `novo` cria o rascunho; `/fluxos/:id/execucoes` e `/editar` ficam
                        no bloco /whatsapp abaixo (o ranking de rotas resolve os caminhos mais longos antes). */}
                    <Route path="/whatsapp/fluxos/:flowId" element={<ProtectedRoute><PermissionRoute permission="wpp.view"><WppModule /></PermissionRoute></ProtectedRoute>}>
                        <Route index element={<WppFlowEditor />} />
                    </Route>

                    {/* SMS: editor de flow em tela cheia (sem sidebar/header), como o editor de fluxos do WhatsApp. */}
                    <Route path="/sms/flows/:id" element={<ProtectedRoute><PermissionRoute permission="sms.flows.edit"><SmsFlowEditor /></PermissionRoute></ProtectedRoute>} />

                    {/* Rotas protegidas com layout */}
                    <Route
                        element={
                            <ProtectedRoute>
                                <MainLayout />
                            </ProtectedRoute>
                        }
                    >
                        {/* Dashboard v2 substitui a antiga (mesma permissão); a antiga fica em /dashboard-antiga como plano B */}
                        <Route path="/dashboard" element={<DashboardV2 />} />
                        <Route path="/dashboard-v2" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard-antiga" element={<Dashboard />} />
                        <Route path="/dominios" element={<Dominios />} />
                        <Route path="/usuarios" element={<Usuarios />} />
                        <Route path="/projetos" element={<Projetos />} />
                        <Route path="/projetos/:id" element={<OwnerOrPartnerRoute><ProjectDetails /></OwnerOrPartnerRoute>} />
                        <Route path="/conexoes" element={<Conexoes />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/chatpion" element={<Chatpions />} />
                        <Route path="/custom-targeting" element={<CustomTargeting />} />

                        {/* Rotas Meta */}
                        <Route path="/meta/campaigns" element={<PermissionRoute permission="meta.campaigns.view"><MetaCampaigns /></PermissionRoute>} />
                        <Route path="/meta/direct" element={<PermissionRoute permission="meta.campaigns.view"><MetaDirect /></PermissionRoute>} />
                        <Route path="/meta/overall" element={<PermissionRoute permission="meta.campaigns.view"><MetaOverall /></PermissionRoute>} />
                        <Route path="/meta/overall-v2" element={<PermissionRoute permission="meta.campaigns.view"><MetaOverallV2 /></PermissionRoute>} />
                        <Route path="/meta/flow" element={<PermissionRoute permission="meta.flow_reports.view"><MetaFlow /></PermissionRoute>} />
                        <Route path="/meta/campaign-history" element={<PermissionRoute permission="meta.campaign_history.view"><MetaCampaignHistory /></PermissionRoute>} />
                        <Route path="/meta/accounts" element={<PermissionRoute permission="meta.campaigns.view"><AccountReport /></PermissionRoute>} />

                        <Route path="/meta/campaign-links" element={<PermissionRoute permission="meta.campaigns.view"><CampaignLinks /></PermissionRoute>} />
                        <Route path="/meta/gerenciador" element={<PermissionRoute permission="meta.campaigns.view"><GerenciadorPage /></PermissionRoute>} />
                        <Route path="/meta/optimizer" element={<RedirectToGerenciadorTab tab="direto" />} />
                        <Route path="/meta/optimizer/nova-campanha" element={<PermissionRoute permission="meta.campaigns.view"><CreateCampaignPage /></PermissionRoute>} />
                        <Route path="/meta/esteiras" element={<RedirectToGerenciadorTab tab="automatico" />} />
                        <Route path="/meta/esteiras/nova" element={<PermissionRoute permission="meta.campaigns.view"><EsteiraWizardPage /></PermissionRoute>} />
                        <Route path="/meta/esteiras/:id/editar" element={<PermissionRoute permission="meta.campaigns.view"><EsteiraWizardPage /></PermissionRoute>} />
                        <Route path="/meta/esteiras/:id" element={<PermissionRoute permission="meta.campaigns.view"><EsteiraDetailPage /></PermissionRoute>} />

                        {/* Rotas Google Ads */}
                        <Route path="/google/conexoes" element={<GoogleConexoes />} />
                        <Route path="/google/campaigns" element={<GoogleCampaigns />} />
                        <Route path="/google/adgroups" element={<GoogleAdGroups />} />
                        <Route path="/google/ads" element={<GoogleAds />} />
                        <Route path="/google/placements" element={<GooglePlacements />} />

                        {/* GAM */}
                        <Route path="/gam/blocks" element={<GamBlocks />} />
                        <Route path="/gam/setup" element={<ConnectaSetups />} />
                        <Route path="/gam/advertisers" element={<PermissionRoute permission="gam.advertisers.view"><GamAdvertisers /></PermissionRoute>} />
                        <Route path="/gam/advertisers/analise" element={<PermissionRoute permission="gam.advertisers.view"><AdvertiserAnalysis /></PermissionRoute>} />

                        {/* Connecta Setup */}
                        <Route path="/gam/setup/:domainId" element={<SetupConnecta />} />

                        {/* Push Notification */}
                        <Route path="/push-notification/setup" element={<PermissionRoute permission="push.setup.manage"><Setup /></PermissionRoute>} />
                        <Route path="/push-notification/campaigns" element={<PermissionRoute permission="push.campaigns.view"><Campaigns /></PermissionRoute>} />
                        <Route path="/push-notification/campaign/create" element={<PermissionRoute permission="push.campaigns.create"><CampaignCreate /></PermissionRoute>} />
                        <Route path="/push-notification/leads" element={<PermissionRoute permission="push.leads.view"><Leads /></PermissionRoute>} />

                        <Route path="/push-notification/flows" element={<PermissionRoute permission="push.flows.view"><FlowList /></PermissionRoute>} />
                        <Route path="/push-notification/flows/create" element={<PermissionRoute permission="push.flows.create"><FlowCreate /></PermissionRoute>} />
                        <Route path="/push-notification/flows/:flowId" element={<PermissionRoute permission="push.flows.edit"><FlowBuilder /></PermissionRoute>} />
                        <Route path="/push-notification/flows/:flowId/funnel" element={<PermissionRoute permission="push.flows.view"><FlowFunnel /></PermissionRoute>} />
                        <Route path="/push-notification/docs" element={<PushDocs />} />

                        {/* Push Capture — owner-only enquanto está em rollout */}
                        <Route path="/push-capture/domains" element={<PermissionRoute permission="push_capture.domains.view"><PushCaptureDomainsList /></PermissionRoute>} />
                        <Route path="/push-capture/domains/:id" element={<PermissionRoute permission="push_capture.domains.view"><PushCaptureDomainDetail /></PermissionRoute>} />
                        <Route path="/push-capture/groups" element={<PermissionRoute permission="push_capture.groups.view"><PushCaptureGroupsList /></PermissionRoute>} />
                        <Route path="/push-capture/templates" element={<PermissionRoute permission="push_capture.domains.view"><PushCaptureTemplatesList /></PermissionRoute>} />
                        <Route path="/push-capture/templates/:id" element={<PermissionRoute permission="push_capture.domains.view"><PushCaptureTemplateBuilder /></PermissionRoute>} />
                        <Route path="/push-notification" element={<PermissionRoute permission="push.dashboard.view"><Dash /></PermissionRoute>} />

                        {/* SMS */}
                        <Route path="/sms" element={<PermissionRoute permission="sms.dashboard.view"><SmsDashboard /></PermissionRoute>} />
                        <Route path="/sms/dashboard" element={<PermissionRoute permission="sms.dashboard.view"><SmsDashboard /></PermissionRoute>} />
                        <Route path="/sms/broadcasts" element={<PermissionRoute permission="sms.campaigns.view"><SmsBroadcasts /></PermissionRoute>} />
                        <Route path="/sms/broadcasts/create" element={<PermissionRoute permission="sms.campaigns.create"><SmsBroadcastCreate /></PermissionRoute>} />
                        <Route path="/sms/broadcasts/:id" element={<PermissionRoute permission="sms.campaigns.view"><SmsBroadcastDetail /></PermissionRoute>} />
                        <Route path="/sms/campaigns" element={<PermissionRoute permission="sms.campaigns.view"><SmsCampaigns /></PermissionRoute>} />
                        <Route path="/sms/campaigns/create" element={<PermissionRoute permission="sms.campaigns.create"><SmsCampaignCreate /></PermissionRoute>} />
                        <Route path="/sms/campaigns/:id" element={<PermissionRoute permission="sms.campaigns.view"><SmsCampaignDetail /></PermissionRoute>} />
                        <Route path="/sms/flows" element={<PermissionRoute permission="sms.flows.view"><SmsFlows /></PermissionRoute>} />
                        {/* "Novo flow" virou modal na lista */}
                        <Route path="/sms/flows/create" element={<Navigate to="/sms/flows" replace />} />
                        <Route path="/sms/leads" element={<PermissionRoute permission="sms.leads.view"><SmsLeads /></PermissionRoute>} />
                        <Route path="/sms/leads/:domainId/:phone" element={<PermissionRoute permission="sms.leads.view"><SmsLeadDetail /></PermissionRoute>} />
                        <Route path="/sms/providers" element={<PermissionRoute permission="sms.providers.manage"><SmsProviders /></PermissionRoute>} />
                        <Route path="/sms/domain-keys" element={<PermissionRoute permission="sms.providers.manage"><SmsDomainKeys /></PermissionRoute>} />
                        <Route path="/sms/costs" element={<PermissionRoute permission="sms.costs.view"><SmsCosts /></PermissionRoute>} />
                        <Route path="/sms/reports" element={<PermissionRoute permission="sms.reports.view"><SmsReports /></PermissionRoute>} />
                        <Route path="/sms/roi" element={<PermissionRoute permission="sms.reports.view"><SmsRoi /></PermissionRoute>} />
                        <Route path="/sms/roi/flow/:funnelId" element={<PermissionRoute permission="sms.reports.view"><SmsFlowDetail /></PermissionRoute>} />


                        {/* Relatórios de e-mail — as oito telas num módulo só, para compartilhar filtros e preferências */}
                        <Route path="/email" element={<Navigate to="/email/geral" replace />} />
                        <Route path="/email/*" element={<PermissionRoute permission="email.reports.view"><EmailV1 /></PermissionRoute>} />

                        {/* WhatsApp (ms-wpp) v2 (plano-front §4): cada item do menu é uma tela própria (WppScreen); a WppModule
                            só compartilha projeto selecionado (memória + localStorage; nada na URL) + tempo real, sem cabeçalho nem abas.
                            Menu: Visão geral, Números, Grupos, Contatos, Disparos, Fluxos, Inbox, IA, Configurações.
                            Rotas antigas de itens que viraram blocos de outra tela redirecionam para ela.
                            O editor de fluxo (/whatsapp/fluxos/novo e /:flowId) fica fora do layout, acima. */}
                        <Route path="/whatsapp" element={<PermissionRoute permission="wpp.view"><WppModule /></PermissionRoute>}>
                            <Route index element={<WppOverview />} />
                            <Route path="alertas" element={<WppRedirect to="/whatsapp" />} />
                            <Route path="numeros" element={<WppNumbers />} />
                            <Route path="numeros/:numberId" element={<WppNumberDetail />} />
                            <Route path="pools" element={<WppRedirect to="/whatsapp/numeros" />} />
                            <Route path="pools/:poolId" element={<WppPoolDetail />} />
                            <Route path="grupos" element={<WppGroups />} />
                            <Route path="grupos/:groupId" element={<WppGroupDetail />} />
                            <Route path="conjuntos" element={<WppRedirect to="/whatsapp/grupos" />} />
                            <Route path="conjuntos/:setId" element={<WppGroupSetDetail />} />
                            <Route path="comunidades" element={<WppRedirect to="/whatsapp/grupos" set={{ secao: 'comunidades' }} />} />
                            <Route path="contatos" element={<WppContacts />} />
                            <Route path="contatos/:contactId" element={<WppContactDetail />} />
                            <Route path="listas" element={<WppRedirect to="/whatsapp/contatos" set={{ ver: 'listas' }} />} />
                            <Route path="listas/:listId" element={<WppListDetail />} />
                            <Route path="segmentos" element={<WppRedirect to="/whatsapp/contatos" set={{ ver: 'segmentos' }} />} />
                            <Route path="segmentos/novo" element={<WppSegmentEditor />} />
                            <Route path="segmentos/:segmentId" element={<WppSegmentEditor />} />
                            <Route path="importacoes" element={<WppRedirect to="/whatsapp/contatos" set={{ importacoes: '1' }} />} />
                            <Route path="configuracoes" element={<WppSettings />} />
                            <Route path="campos" element={<WppRedirect to="/whatsapp/configuracoes" set={{ secao: 'campos' }} />} />
                            <Route path="webhooks" element={<WppRedirect to="/whatsapp/configuracoes" set={{ secao: 'webhooks' }} />} />
                            <Route path="templates" element={<WppRedirect to="/whatsapp/configuracoes" set={{ secao: 'templates' }} />} />
                            <Route path="servidores" element={<WppRedirect to="/whatsapp/configuracoes" set={{ secao: 'servidores' }} />} />
                            <Route path="disparos" element={<WppBroadcasts />} />
                            <Route path="disparos/novo" element={<WppBroadcastEditor />} />
                            <Route path="disparos/:broadcastId" element={<WppBroadcastDetail />} />
                            <Route path="disparos/:broadcastId/editar" element={<WppBroadcastEditor />} />
                            <Route path="disparos/:broadcastId/relatorio" element={<WppBroadcastReportRedirect />} />
                            <Route path="fluxos" element={<WppFlows />} />
                            <Route path="fluxos/:flowId/editar" element={<WppFlowEditRedirect />} />
                            <Route path="fluxos/:flowId/execucoes" element={<WppFlowExecutions />} />
                            <Route path="inbox" element={<WppInbox />} />
                            <Route path="ia" element={<WppAi />} />
                        </Route>

                        {/* Rewards */}
                        <Route path="/rewards" element={<PermissionRoute permission="rewards.dashboard.view"><RewardsDashboard /></PermissionRoute>} />
                        <Route path="/rewards/dashboard" element={<PermissionRoute permission="rewards.dashboard.view"><RewardsDashboard /></PermissionRoute>} />
                        <Route path="/rewards/list" element={<PermissionRoute permission="rewards.list.view"><RewardsList /></PermissionRoute>} />
                        <Route path="/rewards/new" element={<PermissionRoute permission="rewards.list.create"><RewardEdit /></PermissionRoute>} />
                        <Route path="/rewards/:rewardId/edit" element={<PermissionRoute permission="rewards.list.edit"><RewardEdit /></PermissionRoute>} />
                        <Route path="/rewards/domains" element={<PermissionRoute permission="rewards.domains.manage"><RewardsDomains /></PermissionRoute>} />
                        <Route path="/rewards/capture-keys" element={<PermissionRoute permission="rewards.capture_keys.manage"><RewardsCaptureKeys /></PermissionRoute>} />
                        <Route path="/rewards/leads" element={<PermissionRoute permission="rewards.dashboard.view"><RewardsLeads /></PermissionRoute>} />

                        {/* Analytics */}
                        <Route path="/analytics/dashboard" element={<AnalyticsDashboard />} />
                        <Route path="/analytics/flow" element={<LeadJourney />} />

                        <Route path="/rotative-links" element={<RotativeLinks />} />
                        <Route path="/rotative-links/create" element={<CreateRotativeLink />} />
                        <Route path="/rotative-links/edit/:id" element={<EditRotativeLink />} />
                        <Route path="/rotative-links/create-auto" element={<CreateAutoRotativeLink />} />
                        <Route path="/rotative-links/edit-auto/:id" element={<EditRotativeLink />} />
                        <Route path="/apps" element={<OwnerOrPartnerRoute><Apps /></OwnerOrPartnerRoute>} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/notifications-goals" element={<NotificationsGoals />} />
                        <Route path="/notifications-channels" element={<NotificationsChannels />} />
                        <Route path="/top-url" element={<TopUrl />} />


                        <Route path="/reports/navigation" element={<PermissionRoute permission="reports.navigation.view"><NavigationReport /></PermissionRoute>} />
                        <Route path="/reports/lead-lifetime" element={<PermissionRoute permission="reports.lead_lifetime.view"><LeadLifetime /></PermissionRoute>} />

                        {/* Permissões */}
                        <Route path="/funcionarios/:id/permissoes" element={<EmployeePermissions />} />
                        <Route path="/grupos-permissao" element={<PermissionGroups />} />
                        <Route path="/gerenciar-menus" element={<OwnerOrPartnerRoute><MenuManagement /></OwnerOrPartnerRoute>} />
                    </Route>

                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                </Suspense>
                <Toaster />
            </BrowserRouter>
        </QueryClientProvider>
        </ThemeProvider>
    );
}

export default App;